import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    CalendarDays,
    Check,
    ImageOff,
    Link2,
    Mail,
    MessageCircle,
    Share2,
} from "lucide-react";
import { formatBlogDate } from "../lib/publicBlogs";

const BlogCard = ({ blog, featured = false }) => {
    const [imageError, setImageError] = useState(false);
    const [copied, setCopied] = useState(false);
    const date = formatBlogDate(blog.publishedAt);
    const shareUrl = blog.url || `https://vilengineering.com/blogs/${blog.slug}`;
    const blogPath = `/blogs/${blog.slug}`;
    const shareText = `${blog.title} - ${shareUrl}`;
    const encodedUrl = encodeURIComponent(shareUrl);
    const encodedTitle = encodeURIComponent(blog.title);
    const encodedText = encodeURIComponent(shareText);

    const copyShareLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (err) {
            setCopied(false);
        }
    };

    return (
        <article
            className={`group glass corner-accent overflow-hidden card-lift flex flex-col h-full ${
                featured ? "lg:grid lg:grid-cols-[1.08fr_0.92fr]" : ""
            }`}
            data-testid={`blog-card-${blog.id}`}
        >
            <div className={`relative overflow-hidden bg-slate-100 ${featured ? "h-64 md:h-72 lg:h-full lg:max-h-[360px]" : "h-44 md:h-48"}`}>
                {blog.image && !imageError ? (
                    <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover img-zoom"
                        loading="lazy"
                        onError={() => setImageError(true)}
                    />
                ) : (
                    <div className="h-full w-full flex flex-col items-center justify-center gap-2 text-slate-400">
                        <ImageOff className="w-8 h-8" />
                        <span className="text-xs font-mono uppercase tracking-widest">
                            Image unavailable
                        </span>
                    </div>
                )}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(10,10,12,0.02) 20%, rgba(10,10,12,0.38) 100%)",
                    }}
                />
                <div className="absolute left-4 top-4 bg-white/95 px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-[#E53935] shadow-sm">
                    {blog.categoryName}
                </div>
            </div>

            <div className={`p-6 flex flex-col flex-1 ${featured ? "md:p-8" : ""}`}>
                <div className="flex items-center gap-2 text-xs text-slate-500 font-mono uppercase tracking-widest mb-4">
                    <CalendarDays className="w-4 h-4 text-[#0E4DA4]" />
                    {date || "Latest insight"}
                </div>

                <h3
                    className={`font-display font-black text-slate-900 leading-tight ${
                        featured ? "text-2xl md:text-3xl" : "text-xl"
                    }`}
                >
                    {blog.title}
                </h3>

                <p className={`text-slate-600 leading-relaxed mt-4 ${featured ? "text-base" : "text-sm"}`}>
                    {blog.excerpt}
                </p>

                <div className="mt-6 pt-5 border-t border-slate-200">
                    <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-slate-500 mb-3">
                        <Share2 className="w-4 h-4 text-[#E53935]" />
                        Share
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <ShareLink
                            href={`https://wa.me/?text=${encodedText}`}
                            label="WhatsApp"
                            title="Share on WhatsApp"
                            icon={<MessageCircle className="w-4 h-4" />}
                        />
                        <ShareLink
                            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
                            label="in"
                            title="Share on LinkedIn"
                        />
                        <ShareLink
                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
                            label="f"
                            title="Share on Facebook"
                        />
                        <ShareLink
                            href={`mailto:?subject=${encodedTitle}&body=${encodedText}`}
                            label="Email"
                            title="Share by email"
                            icon={<Mail className="w-4 h-4" />}
                        />
                        <button
                            type="button"
                            onClick={copyShareLink}
                            className="h-9 min-w-9 px-3 inline-flex items-center justify-center gap-2 border border-slate-200 bg-white text-xs font-mono uppercase tracking-widest text-slate-600 hover:border-[#E53935] hover:text-[#E53935] transition"
                            title="Copy blog link"
                            aria-label="Copy blog link"
                            data-testid={`blog-share-copy-${blog.id}`}
                        >
                            {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
                            <span className="sr-only">{copied ? "Copied" : "Copy"}</span>
                        </button>
                    </div>
                </div>

                <Link
                    to={blogPath}
                    className="btn-secondary mt-6 self-start !py-2.5"
                    data-testid={`blog-read-${blog.id}`}
                >
                    Read Blog <ArrowUpRight className="w-4 h-4" />
                </Link>
            </div>
        </article>
    );
};

const ShareLink = ({ href, label, title, icon }) => (
    <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="h-9 min-w-9 px-3 inline-flex items-center justify-center gap-2 border border-slate-200 bg-white text-xs font-mono uppercase tracking-widest text-slate-600 hover:border-[#E53935] hover:text-[#E53935] transition"
        title={title}
        aria-label={title}
    >
        {icon || <span className="font-display font-black normal-case text-sm">{label}</span>}
        {icon && <span className="sr-only">{label}</span>}
    </a>
);

export default BlogCard;
